import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getMyProfile, updateMyProfile } from "../api/profiles";
import {
  DressCode,
  DRESS_CODE_LABELS,
  VibeTag,
  VIBE_TAG_LABELS,
} from "../types";
import { useAuth } from "../context/AuthContext";
import Loading from "../components/Loading";
import ErrorMessage from "../components/ErrorMessage";
import TogglePill from "../components/TogglePill";
import AccountSettingsPanel from "../components/AccountSettingsPanel";

const DRESS_CODES = Object.keys(DRESS_CODE_LABELS) as DressCode[];
const VIBE_TAGS = Object.keys(VIBE_TAG_LABELS) as VibeTag[];

const toggleItem = <T extends string>(list: T[], item: T): T[] =>
  list.includes(item) ? list.filter((x) => x !== item) : [...list, item];

const Profile: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [stripeReturnNote, setStripeReturnNote] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [dressComfort, setDressComfort] = useState<DressCode[]>([]);
  const [vibePreferences, setVibePreferences] = useState<VibeTag[]>([]);
  const [is21Plus, setIs21Plus] = useState(false);
  const [priceMinUsd, setPriceMinUsd] = useState("0");
  const [priceMaxUsd, setPriceMaxUsd] = useState("");
  const [instagram, setInstagram] = useState("");

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login");
      return;
    }

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await getMyProfile();
        setDressComfort(data.dress_comfort || []);
        setVibePreferences(data.vibe_preferences || []);
        setIs21Plus(!!data.is_21_plus);
        setPriceMinUsd(String((data.price_min_cents || 0) / 100));
        setPriceMaxUsd(
          data.price_max_cents != null ? String(data.price_max_cents / 100) : ""
        );
        setInstagram(data.instagram_handle || "");
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load profile");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const stripeParam = searchParams.get("stripe");
    if (!stripeParam) return;
    setStripeReturnNote(
      "Stripe setup continues on a wedding Manage page, under Link Stripe Account. Existing connections stay linked to your account."
    );
    const next = new URLSearchParams(searchParams);
    next.delete("stripe");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");

    const minCents = Math.max(0, Math.round(Number(priceMinUsd || 0) * 100));
    const maxCents =
      priceMaxUsd === ""
        ? null
        : Math.max(0, Math.round(Number(priceMaxUsd) * 100));

    try {
      await updateMyProfile({
        dress_comfort: dressComfort,
        vibe_preferences: vibePreferences,
        is_21_plus: is21Plus,
        price_min_cents: minCents,
        price_max_cents: maxCents,
        instagram_handle: instagram.trim() || null,
      });
      setSuccess("Profile saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) return <Loading />;
  if (!user) return null;

  return (
    <div className="page profile-page">
      <h1>Profile</h1>
      <div className="golden-thread" aria-hidden="true" />
      <p className="form-hint" style={{ textAlign: "center" }}>
        Signed in as {user.email}
      </p>

      <p className="form-hint" style={{ textAlign: "center", maxWidth: 640, margin: "0 auto 1.5rem" }}>
        {dressComfort.length > 0 && vibePreferences.length > 0
          ? "Your profile is complete — you're ready to request an invite to a wedding."
          : "Complete this profile before requesting an invite to a wedding."}
      </p>

      {error && <ErrorMessage message={error} />}
      {stripeReturnNote && (
        <div className="success-message" style={{ marginBottom: "1.5rem" }}>
          <p>{stripeReturnNote}</p>
        </div>
      )}
      {success && (
        <div className="success-message" style={{ marginBottom: "1.5rem" }}>
          <p>{success}</p>
        </div>
      )}

      <form className="form profile-goer-form" style={{ maxWidth: 640, margin: "0 auto" }} onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Dress Comfort</label>
          <div className="pill-group">
            {DRESS_CODES.map((d) => (
              <TogglePill
                key={d}
                label={DRESS_CODE_LABELS[d]}
                selected={dressComfort.includes(d)}
                onToggle={() => setDressComfort((prev) => toggleItem(prev, d))}
              />
            ))}
          </div>
        </div>

        <div className="form-group">
          <label>Vibe Preferences</label>
          <div className="pill-group">
            {VIBE_TAGS.map((v) => (
              <TogglePill
                key={v}
                label={VIBE_TAG_LABELS[v]}
                selected={vibePreferences.includes(v)}
                onToggle={() => setVibePreferences((prev) => toggleItem(prev, v))}
              />
            ))}
          </div>
        </div>

        <div className="form-group">
          <label>Are you 21 or older?</label>
          <div className="pill-group">
            <TogglePill
              label="Yes"
              selected={is21Plus}
              onToggle={() => setIs21Plus(true)}
            />
            <TogglePill
              label="No"
              selected={!is21Plus}
              onToggle={() => setIs21Plus(false)}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="price-min">Min Ticket Budget (USD)</label>
            <input
              id="price-min"
              type="number"
              min="0"
              step="1"
              value={priceMinUsd}
              onChange={(e) => setPriceMinUsd(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="price-max">Max Ticket Budget (USD)</label>
            <input
              id="price-max"
              type="number"
              min="0"
              step="1"
              placeholder="No max"
              value={priceMaxUsd}
              onChange={(e) => setPriceMaxUsd(e.target.value)}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="instagram">Instagram Handle (Optional)</label>
          <input
            id="instagram"
            type="text"
            placeholder="@yourhandle"
            value={instagram}
            onChange={(e) => setInstagram(e.target.value)}
          />
        </div>

        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? "Saving..." : "Save Profile"}
        </button>
      </form>

      <AccountSettingsPanel />
    </div>
  );
};

export default Profile;
